<template>
    <div>
        <v-col cols="12">
            <h2 class="mt-4 mb-0">{{ t('acl::page.title.acl-resources') }}</h2>

            <b-datatable
                :key="datatableKey"
                :initial-sort="initialSort"
                :columns="columns"
                :endpoint="listRoute"
                :hidden-columns="hiddenColumns"
                :multi-sort="false"
                has-filter-selector
                item-key="id"
            >
                <template #filters="{ filters }">
                    <v-col cols="4" class="pb-0">
                        <b-filter-input
                            v-model="filters.filters.tag"
                            :label="t('acl::grid.label.tag')"
                        ></b-filter-input>
                    </v-col>
                    <v-col cols="4" class="pb-0">
                        <b-filter-input
                            v-model="filters.filters.name"
                            :label="t('acl::grid.label.name')"
                        ></b-filter-input>
                    </v-col>
                    <v-col cols="4" class="pb-0">
                        <b-filter-input
                            v-model="filters.filters.system_name"
                            :label="t('acl::grid.label.system_name')"
                        ></b-filter-input>
                    </v-col>
                    <v-col cols="4" class="pb-0">
                        <b-filter-input v-model="filters.filters.uri" :label="t('acl::grid.label.uri')">
                        </b-filter-input>
                    </v-col>
                    <v-col cols="4" class="pb-0">
                        <b-filter-select
                            v-model="filters.filters.allowed_for_guests"
                            :items="filterItems"
                            :label="t('acl::grid.allowed_for_guests')"
                            :clearable="filters.filters.allowed_for_guests !== null"
                            item-text="label"
                            item-value="value"
                        >
                        </b-filter-select>
                    </v-col>
                    <v-col cols="4" class="pb-0">
                        <b-filter-select
                            v-model="filters.filters.allowed_for_all"
                            :items="filterItems"
                            :label="t('acl::grid.allowed_for_all_groups')"
                            :clearable="filters.filters.allowed_for_all !== null"
                            item-text="label"
                            item-value="value"
                        >
                        </b-filter-select>
                    </v-col>
                    <v-col cols="4" class="pb-0">
                        <b-filter-select
                            v-model="filters.filters.api_route"
                            :items="filterItems"
                            :label="t('acl::grid.api_route')"
                            :clearable="filters.filters.api_route !== null"
                            item-text="label"
                            item-value="value"
                        >
                        </b-filter-select>
                    </v-col>
                    <v-col cols="4" class="pb-0">
                        <b-filter-select
                            v-model="filters.filters.visible_permission"
                            :items="filterItems"
                            :label="t('acl::grid.visible_permission')"
                            :clearable="filters.filters.visible_permission !== null"
                            item-text="label"
                            item-value="value"
                        >
                        </b-filter-select>
                    </v-col>
                    <v-col cols="4" class="pb-0">
                        <b-filter-select
                            v-model="filters.filters.audit_disabled"
                            :items="filterItems"
                            :label="t('acl::grid.audit_disabled')"
                            :clearable="filters.filters.audit_disabled !== null"
                            item-text="label"
                            item-value="value"
                        >
                        </b-filter-select>
                    </v-col>
                </template>
                <template #[`item.allowed_for_guests`]="{ item }">
                    <div class="grid-status">
                        <span>
                            {{ item.allowed_for_guests ? t('default::label.yes') : t('default::label.no') }}
                        </span>
                    </div>
                </template>
                <template #[`item.allowed_for_all`]="{ item }">
                    <div class="grid-status">
                        <span>
                            {{ item.allowed_for_all ? t('default::label.yes') : t('default::label.no') }}
                        </span>
                    </div>
                </template>
                <template #[`item.api_route`]="{ item }">
                    <div class="grid-status">
                        <span>
                            {{ item.api_route ? t('default::label.yes') : t('default::label.no') }}
                        </span>
                    </div>
                </template>
                <template #[`item.visible_permission`]="{ item }">
                    <div class="grid-status">
                        <span>
                            {{ item.visible_permission ? t('default::label.yes') : t('default::label.no') }}
                        </span>
                    </div>
                </template>
                <template #[`item.audit_disabled`]="{ item }">
                    <div class="grid-status">
                        <span>
                            {{ item.audit_disabled ? t('default::label.yes') : t('default::label.no') }}
                        </span>
                    </div>
                </template>
                <template #[`item.actions`]="{ item }">
                    <div class="table-actions">
                        <v-tooltip v-if="can('api.v1.acl-routes.update')" top transition="v-fab-transition">
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    color="disabled-color"
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="$root.goTo(editItemUrl(item))"
                                >
                                    <v-icon>mdi-pencil</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('default::button.edit') }}</span>
                        </v-tooltip>
                        <b-delete
                            v-if="can('api.v1.acl-routes.destroy')"
                            :path="deleteItemUrl(item)"
                            :text="t('default::confirmation.deletion')"
                            :title="t('default::confirmation.general')"
                            icon="mdi-trash-can"
                            @deleted="triggerDeleted"
                        >
                        </b-delete>
                    </div>
                </template>
            </b-datatable>
        </v-col>
    </div>
</template>

<script>
export default {
    props: {
        listRoute: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            datatableKey: 0,
            hiddenColumns: ['id'],
            filterItems: [
                { label: t('default::label.all'), value: null },
                { label: t('default::labels.no'), value: 0 },
                { label: t('default::labels.yes'), value: 1 },
            ],
            initialSort: 'tag,name',
            columns: [
                {
                    text: t('default::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('acl::grid.label.tag'),
                    value: 'tag',
                    sortable: true,
                    width: 150,
                },
                {
                    text: t('acl::grid.label.name'),
                    value: 'name',
                    sortable: true,
                    width: 250,
                },
                {
                    text: t('acl::grid.label.system_name'),
                    value: 'system_name',
                    sortable: true,
                    width: 250,
                },
                {
                    text: t('acl::grid.label.uri'),
                    value: 'uri',
                    sortable: true,
                    width: 300,
                },
                {
                    text: t('acl::grid.allowed_for_guests'),
                    value: 'allowed_for_guests',
                    sortable: true,
                    width: 100,
                },
                {
                    text: t('acl::grid.allowed_for_all'),
                    value: 'allowed_for_all',
                    width: 100,
                    sortable: true,
                },
                {
                    text: t('acl::grid.api_route'),
                    value: 'api_route',
                    width: 100,
                    sortable: true,
                },
                {
                    text: t('acl::grid.visible_permission'),
                    value: 'visible_permission',
                    width: 100,
                    sortable: true,
                },
                {
                    text: t('acl::grid.audit_disabled'),
                    value: 'audit_disabled',
                    width: 100,
                    sortable: true,
                },
                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: null,
                    align: 'end',
                    width: 140,
                },
            ],
        };
    },
    methods: {
        editItemUrl(item) {
            return route('acl-routes.get', { acl_route: item.id });
        },
        deleteItemUrl(item) {
            return route('api.v1.acl-routes.destroy', { acl_route: item.id });
        },
        triggerDeleted() {
            this.datatableKey++;
            this.$root.notify('info', t('default::notification.deleted'));
        },
    },
};
</script>

<style>
.v-application--is-ltr .v-list-item__action:first-child,
.v-application--is-ltr .v-list-item__icon:first-child {
    margin-right: 5px !important;
}
</style>
